import { Injectable } from '@angular/core';
import { BigNumber } from 'bignumber.js';
import Qty from 'js-quantities';
import { Observable, Subject } from 'rxjs';
import { IUnitDef } from 'src/app/core/dto';

@Injectable({
  providedIn: 'root',
})
export class UnitsService {
  private currentInchesKey_ = 'in';
  private revision_ = 0;
  private readonly changed_ = new Subject<string>();

  /**
   * Emits the new key whenever the effective display units change. Consumers
   * that cache formatted output (ag-grid, d3/echarts) subscribe to re-render;
   * template bindings pick the change up through {@link revision}.
   */
  readonly unitsChanged$: Observable<string> = this.changed_.asObservable();

  /**
   * Bumped on every units change. The units pipes fold this into their memo
   * key so a change invalidates cached formatting.
   */
  get revision(): number {
    return this.revision_;
  }

  // Driven by UserPreferencesService — combines system default with the
  // current user's override.
  get currentInchesKey(): string {
    return this.currentInchesKey_;
  }

  set currentInchesKey(key: string) {
    if (key === this.currentInchesKey_) {
      return;
    }
    this.currentInchesKey_ = key;
    this.revision_++;
    this.changed_.next(key);
  }
  units = [
    { key: 'in', system: 'imperial', base: 'in', title: 'Inches' },
    {
      key: 'ffi',

      system: 'imperial',
      base: 'in',
      title: 'Feet Fractional Inch',
    },
    {
      key: 'fdi',
      system: 'imperial',
      base: 'in',
      title: 'Feet Decimal Inch',
    },
    { key: 'ft', system: 'imperial', base: 'in', title: 'Feet' },
    { key: 'mm', system: 'metric', base: 'in', title: 'Millimeter' },
    { key: 'cm', system: 'metric', base: 'in', title: 'Centimeter' },
    { key: 'm', system: 'metric', base: 'in', title: 'Meter' },
    { key: 'ft', system: 'imperial', base: 'ft', title: 'Feet' },
    { key: 'm', system: 'metric', base: 'ft', title: 'Meter' },
    { key: 'fpm', system: 'imperial', base: 'fpm', title: 'Feet/Minute' },
    { key: 'mpm', system: 'metric', base: 'fpm', title: 'Meter/Minute' },
    { key: 'lbs', system: 'imperial', base: 'lbs', title: 'Pounds' },
    { key: 'kg', system: 'metric', base: 'lbs', title: 'Kilograms' },
  ];

  constructor() {}

  // This returns the list of units that are equiv to
  getBaseUnits() {
    let baseUnits = [];
    for (let i = 0; i < this.units.length; i++) {
      if (this.units[i].base === 'in') {
        baseUnits.push({
          key: this.units[i].key,
          title: this.units[i].title,
        });
      }
    }
    return baseUnits;
  }

  formatUserUnits(
    input: number,
    inType: string,
    decimals: number,
    hideType: boolean = false,
    outType: string = '',
    ignoreSpecialInches: boolean = false
  ): string {
    if (!input && input !== 0) {
      return '';
    } //if undefined or NaN (null?) but no 0
    if (!decimals || decimals < 0) {
      decimals = 0;
    }

    if (inType === '%') {
      return (
        this.round(input * 100, decimals).toLocaleString(undefined, {
          minimumFractionDigits: decimals,
        }) + (hideType ? '' : '%')
      );
    }
    if (inType === 'min') {
      return (
        this.round(input, decimals).toLocaleString(undefined, {
          minimumFractionDigits: decimals,
        }) + (hideType ? '' : ' min')
      ); //todo:i18n
    }
    if (inType === '') {
      return input + '';
    }

    if (!outType || outType === '') {
      outType = this.getUserUnits(inType);
    }
    return this.formatUnits(input, inType, outType, decimals, hideType, ignoreSpecialInches);
  }

  formatUnits(
    input: number,
    inType: string,
    outType: string,
    decimals: number,
    hideType: boolean,
    ignoreSpecialInches: boolean
  ): string {
    //sanitize inputs
    if (!input) {
      input = 0;
    }
    if (!decimals || decimals < 0) {
      decimals = 0;
    }

    if (inType === outType) {
      return (
        this.round(input, decimals).toLocaleString(undefined, {
          minimumFractionDigits: decimals,
        }) + (hideType ? '' : ' ' + outType)
      );
    }

    if (ignoreSpecialInches && (outType === 'fdi' || outType === 'ffi')) {
      outType = 'in';
    }

    //special formats
    switch (outType) {
      case 'fdi':
        return this.inToFdi(input);
      case 'ffi':
        return this.inToFfi(input);
      case 'mpm':
        let mpm = this.fpmToMpm(input);
        return mpm + (hideType ? '' : ' ' + outType);
    }

    //the rest can just use the js-quantity conversion
    let inputQty = new Qty(`${input}${inType}`);
    let outQty = inputQty.to(outType);
    return (
      this.round(outQty.scalar, decimals).toLocaleString(undefined, {
        minimumFractionDigits: decimals,
      }) + (hideType ? '' : ' ' + outType)
    );
  }

  convertUnits(input: number, inType: string, decimals: number, outType: string = ''): number {
    if (!input) {
      return 0;
    }
    if (!decimals || decimals < 0) {
      decimals = 0;
    }
    if (inType === '%') {
      return this.round(input * 100, decimals);
    }
    if (inType === 'min') {
      return this.round(input, decimals);
    }

    if (!outType) {
      outType = this.getUserUnits(inType);
    }

    if (inType === outType) {
      return this.round(input, decimals);
    }

    // Normalize types for `Qty` conversion.
    switch (outType) {
      case 'ffi':
      case 'fdi': //currently, there is no way this would be anything other than inches on both sides
        // outType = 'in';
        // break;
        return this.round(input, decimals);
      case 'mpm':
        outType = 'm';
        break;
      case 'fpm':
        outType = 'ft';
        break;
    }
    switch (inType) {
      case 'mpm':
        inType = 'm';
        break;
      case 'fpm':
        inType = 'ft';
        break;
    }

    let inputQty = new Qty(`${input}${inType}`);
    let outQty = inputQty.to(outType);
    return this.round(outQty.scalar, decimals);
  }

  //todo:move to shared math library?
  round(input: number, decimals: number): number {
    let pow = Math.pow(10, decimals);
    let rounded = Math.round(input * pow) / pow;
    if (Math.sign(rounded) === -0) return 0; // fucking javascript
    return rounded;
  }

  inToFfi(input: number): string {
    //ported from vfp DecToFFIn
    let neg = input < 0;
    let length = Math.abs(input);
    let feet = Math.floor(length / 12);
    let inches = Math.floor(length - feet * 12);
    let dec = length - feet * 12 - inches;
    // BigNumber will throw if it's input has more than 15 significant digits
    let x = new BigNumber(dec.toFixed(15));
    let fraction = x.toFraction(64);

    let result = neg ? '-' : '';
    if (feet > 0) {
      result += feet + "' ";
    }
    result += inches;
    if (dec > 0) {
      result += '-' + fraction[0] + '/' + fraction[1];
    }
    result += '"';

    return result;
  }

  inToFdi(input: number): string {
    let neg = input < 0;
    let length = Math.abs(input);
    let feet = Math.floor(length / 12);
    // Round away float noise (102.488 would otherwise render as 8' 6.488000000000006").
    let inches = this.round(length - feet * 12, 4);
    if (inches === 12) {
      feet += 1;
      inches = 0;
    }

    let result = neg ? '-' : '';
    if (feet > 0) {
      result += feet + "' ";
    }
    result += inches + '"';

    return result;
  }

  fpmToMpm(input: number): number {
    let inputQty = new Qty(`${input}ft`);
    let outQty = inputQty.to('m');
    //let pow = Math.pow(10,decimals);
    //let rounded = Math.round(outQty.scalar*pow)/pow;
    let rounded = Math.round(outQty.scalar);
    return rounded;
  }

  shortenBigNumber(input: number, inType: string, decimals: number): string {
    if (input === null) {
      return '';
    }
    if (input === 0) {
      return '0';
    }

    input = this.convertUnits(input, inType, decimals, undefined); //not sure id decimals is correct here

    let abs = Math.abs(input);
    let rounder = Math.pow(10, decimals);
    let isNegative = input < 0;
    let key = '';
    let powers = [
      { key: 'Q', value: Math.pow(10, 15) },
      { key: 'T', value: Math.pow(10, 12) },
      { key: 'B', value: Math.pow(10, 9) },
      { key: 'M', value: Math.pow(10, 6) },
      { key: 'K', value: 1000 },
    ];

    for (let i = 0; i < powers.length; i++) {
      let reduced = abs / powers[i].value;
      reduced = Math.round(reduced * rounder) / rounder;
      if (reduced >= 1) {
        abs = reduced;
        key = powers[i].key;
        break;
      }
    }
    return (isNegative ? '-' : '') + abs + key;
  }

  getUserUnitDef(type: string): IUnitDef {
    if (type === '' || type === 'min' || type === '%' || type === 'ga') {
      // Not a convertible unit type. Just return something benign
      return { key: type, system: 'imperial', base: 'type', title: type };
    }

    let systemBaseUnitKey = this.currentInchesKey;
    let systemUnit = {
      key: 'in',
      system: 'imperial',
      base: 'in',
      title: 'Inches',
    };

    for (let i = 0; i < this.units.length; i++) {
      if (this.units[i].key === systemBaseUnitKey) {
        systemUnit = this.units[i];
        break;
      }
    }

    if (type === systemUnit.base) {
      return systemUnit;
    }

    for (let j = 0; j < this.units.length; j++) {
      if (this.units[j].base === type && this.units[j].system === systemUnit.system) {
        return this.units[j];
      }
    }

    //didn't find a conversion. Give up.
    // should this be considered an exception?
    console.log('no conversion for unit:' + type);
    return { key: type, system: 'imperial', base: 'type', title: type };
  }

  getUserUnits(type: string, ignoreSpecialInches?: boolean): string {
    let unitType = this.getUserUnitDef(type);
    if ((unitType.key === 'ffi' || unitType.key === 'fdi') && ignoreSpecialInches) {
      return 'in';
    }
    return unitType.key;
  }

  getUserDisplayUnits(type: string): string {
    if (type === '%') {
      return '%';
    }
    let userType = this.getUserUnits(type);
    switch (userType) {
      case 'ffi':
        return '';
      case 'fdi':
        return '';
    }
    return userType;
  }

  getUserFromSystem(value: number, unit: string): number {
    // todo: support more base types and more generic rounding
    if (unit === 'ft') {
      return this.convertUnits(value, 'ft', 0);
    }
    return value;
  }

  getSystemFromUser(userValue: number, unit: string): number {
    // todo: support more base types and more generic rounding
    if (unit === 'ft') {
      return this.convertUnits(userValue, this.getUserUnits('ft'), 3, 'ft');
    }
    return userValue;
  }

  // parse a string into inches

  parseLengthEquationToInches(equation: string, defaultUnit: string = ''): number {
    // Check if the equation is empty or not a string
    if (!equation || typeof equation !== 'string') {
      //throw new Error('Invalid input: equation must be a non-empty string');
      return NaN;
    }
    let result = 0;

    // Trim the equation and split by spaces
    const parts = equation.trim().split(/\s+/);

    // check to make sure the first element is a number
    if (isNaN(this.parseLengthToInches(parts[0], defaultUnit))) {
      //throw new Error('Invalid equation: must start with a number');
      return NaN;
    }

    // Process the equation parts
    for (let i = 0; i < parts.length; i++) {
      let current = parts[i];
      if (i < parts.length - 1) {
        // Check if the next part is a unit (in, ft, mm, cm, m)
        // the user added a space between the number and the unit, add it back
        if (['in', 'ft', 'mm', 'cm', 'm'].includes(parts[i + 1])) {
          current = current + parts[i + 1];
          i++;
        }
      }
      // If the current part is a + or - operator
      if (current === '+' || current === '-') {
        // Ensure there's a number after the operator
        if (i + 1 >= parts.length) {
          //throw new Error('Invalid equation: operator must be followed by a number');
          return NaN;
        }

        const nextNumber = this.parseLengthToInches(parts[i + 1], defaultUnit);

        if (isNaN(nextNumber)) {
          //throw new Error(`Invalid number: ${parts[i + 1]} at position ${i + 1}`);
          return NaN;
        }

        // Perform the explicit operation
        if (current === '+') {
          result += nextNumber;
        } else if (current === '-') {
          result -= nextNumber;
        }

        // Skip the number we just processed
        i++;
      }
      // If the current part is a number (implicit addition). We do this so that the parts of a feet fractional inch can be added together after splitting by spaces.
      else {
        const nextNumber = this.parseLengthToInches(current, defaultUnit);

        if (isNaN(nextNumber)) {
          //throw new Error(`Invalid input: ${current} at position ${i} is not a number or valid operator`);
          return NaN;
        }

        // Implicit addition
        result += nextNumber;
      }
    }

    return result;
  }

  parseLengthToInches(value: string, defaultUnit: string = ''): number {
    let isNegative = false;
    if (value === null || value === undefined) {
      return NaN;
    }
    value = value.trim().toLowerCase();
    if (value.startsWith('-')) {
      isNegative = true;
      value = value.slice(1);
    }
    if (value.endsWith('in')) {
      return parseFloat(value.slice(0, -2)) * (isNegative ? -1 : 1);
    } else if (value.endsWith('ft')) {
      return parseFloat(value.slice(0, -2)) * 12 * (isNegative ? -1 : 1);
    } else if (value.endsWith('m')) {
      const cm = this.convertMetricStringToCentimeters(value);
      return this.convertCentimetersToInches(cm) * (isNegative ? -1 : 1);
    } else if (value.endsWith('"') || value.endsWith("'")) {
      return this.convertFeetAndFractionalInchesToDecimalInches(value) * (isNegative ? -1 : 1);
    } else {
      // Check if the string contains any non-numeric characters
      if (/[^0-9.\-]/.test(value)) {
        return NaN;
      } else {
        const number = parseFloat(value);
        if (isNaN(number)) {
          return NaN;
        }
        return this.bareLengthToInches(number, defaultUnit) * (isNegative ? -1 : 1);
      }
    }
  }

  // A bare number ("3000") is inches unless the caller supplies the unit it should be
  // read in — the inline length editors pass the user's display units so a metric
  // user's "3000" means 3000 mm, not 3000 in.
  private bareLengthToInches(value: number, defaultUnit: string): number {
    switch (defaultUnit) {
      case 'mm':
        return value / 25.4;
      case 'cm':
        return value / 2.54;
      case 'm':
        return (value * 100) / 2.54;
      case 'ft':
        return value * 12;
      default:
        // 'in', 'ffi', 'fdi', or unspecified — inches.
        return value;
    }
  }

  // I don't want to accept a null, but we don't have much choice because of the way Angular instantiates components.
  parseLengthToInchesArray(lengthString: string | null): number[] {
    if (!lengthString) {
      return [];
    }
    const parts = lengthString.split(',').map((part) => part);
    return parts.map((part) => this.parseLengthToInches(part));
  }

  parseLengthEquationToInchesArray(lengthString: string | null): number[] {
    if (!lengthString) {
      return [];
    }
    const parts = lengthString.split(',').map((part) => part);
    return parts.map((part) => this.parseLengthEquationToInches(part));
  }

  private convertFeetAndFractionalInchesToDecimalInches(feetAndInches: string): number {
    if (
      feetAndInches.includes('.') &&
      (feetAndInches.includes('-') || feetAndInches.includes('/'))
    ) {
      return NaN;
    }
    if (feetAndInches.includes("'")) {
      const parts = feetAndInches.split("'");
      const feetPart = parseFloat(parts[0]);
      const inchesPart = parts[1];

      if (!isNaN(feetPart)) {
        if (inchesPart) {
          return feetPart * 12 + this.convertFractionalInchesToDecimal(inchesPart);
        } else {
          return feetPart * 12;
        }
      } else {
        return NaN;
      }
    } else {
      // inches only
      return this.convertFractionalInchesToDecimal(feetAndInches);
    }
  }

  private convertFractionalInchesToDecimal(fractionalInches: string): number {
    if (!fractionalInches.endsWith('"')) {
      return NaN;
    }
    if (
      fractionalInches.includes('.') &&
      (fractionalInches.includes('-') || fractionalInches.includes('/'))
    ) {
      return NaN;
    }

    fractionalInches = fractionalInches.slice(0, -1);

    if (fractionalInches.includes('-')) {
      const parts = fractionalInches.split('-');
      const wholePart = parseFloat(parts[0]);
      const fractionalPart = parts[1];

      if (!isNaN(wholePart)) {
        if (fractionalPart.includes('/')) {
          const fractionParts = fractionalPart.split('/');
          const numerator = parseInt(fractionParts[0], 10);
          const denominator = parseInt(fractionParts[1], 10);

          if (!isNaN(numerator) && !isNaN(denominator) && denominator !== 0) {
            return wholePart + numerator / denominator;
          } else {
            return NaN;
          }
        } else {
          return NaN;
        }
      } else {
        return NaN;
      }
    } else if (fractionalInches.includes('/')) {
      const fractionParts = fractionalInches.split('/');
      const numerator = parseInt(fractionParts[0], 10);
      const denominator = parseInt(fractionParts[1], 10);
      if (!isNaN(numerator) && !isNaN(denominator) && denominator !== 0) {
        return numerator / denominator;
      } else {
        return NaN;
      }
    } else {
      const wholePart = parseFloat(fractionalInches);
      if (!isNaN(wholePart)) {
        return wholePart;
      } else {
        return NaN;
      }
    }
  }

  convertMetricStringToCentimeters(metricString: string): number {
    if (!metricString) {
      return NaN;
    }

    const numberPart = metricString.match(/\d+(\.\d+)?/)?.[0];
    if (!numberPart) {
      return NaN;
    }

    const value = parseFloat(numberPart);
    if (isNaN(value)) return NaN;

    const suffix = metricString.slice(numberPart.length).toLowerCase().trim();
    switch (suffix) {
      case 'mm':
        return value / 10;
      case 'cm':
        return value;
      case 'm':
        return value * 100;
      default:
        return NaN;
    }
  }

  convertCentimetersToInches(centimeters: number): number {
    return centimeters / 2.54;
  }
}
